import { useState, forwardRef, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Slide,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  MenuItem,
  Button,
  Menu
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useTireTransactionSearch, exportTireTransactionSearch } from '../../../stores/tireReceiveStore';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { useLanguage } from '../../../utils/i18n';

import {
  getActionStyles,
  findPositionLabel,
  StyledTableCell,
  StyledTableRow,
  HtmlTableContainer,
  HtmlTable
} from '../report/historical/TireHistoricalPage';
import TransactionDetailModal from '../report/historical/TransactionDetailModal';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function TireCardHistory({ open, onClose, serialNumber }) {
  const { t } = useLanguage();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const exportOpen = Boolean(exportAnchorEl);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const getFormattedDate = () => {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };

  const EXPORT_COLUMNS = useMemo(
    () => [
      { field: 'serial_number', headerName: t('tyreItem.serialNumber', 'Serial Number') },
      { field: 'transaction_date', headerName: t('general.date', 'Date') },
      { field: 'transaction_type', headerName: t('tireCardHistory.actionType', 'Action Type') },
      { field: 'tire_spec_name', headerName: t('specification.spec_name', 'Spec Name') },
      { field: 'tire_brand_name', headerName: t('specification.brand', 'Brand') },
      { field: 'tire_size_code_internal', headerName: t('specification.size', 'Size') },
      { field: 'license_plate', headerName: t('tireCardHistory.vehicle', 'Vehicle') },
      { field: 'km_unit', headerName: t('tireCardHistory.kmUnit', 'KM Unit') },
      { field: 'hm_unit', headerName: t('tireCardHistory.hmUnit', 'HM Unit') },
      { field: 'current_position', headerName: t('general.position', 'Position') },
      { field: 'air_pressure', headerName: t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)') },
      { field: 'tread_depth', headerName: t('tireCardHistory.treadDepthMm', 'Tread Depth (mm)') },
      { field: 'mechanic_name', headerName: t('tireCardHistory.mechanicName', 'Mechanic Name') },
      { field: 'tire_damage_name', headerName: t('tireCardHistory.tireDamage', 'Tire Damage') },
      { field: 'cost_repair', headerName: t('tireCardHistory.costRepair', 'Cost Repair') },
      { field: 'vendor_name', headerName: t('tireCardHistory.vendorName', 'Vendor Name') },
      { field: 'work_order_number', headerName: t('tireCardHistory.workOrder', 'Work Order') },
      { field: 'vendor_type', headerName: t('tireCardHistory.vendorType', 'Vendor Type') },
      { field: 'repair_type', headerName: t('tireCardHistory.repairType', 'Repair Type') },
      { field: 'notes', headerName: t('tireCardHistory.notes', 'Notes') },
      { field: 'action_status', headerName: t('tireCardHistory.actionStatus', 'Action Status') },
      { field: 'final_rtd', headerName: t('tireCardHistory.finalRtd', 'Final RTD (mm)') },
      { field: 'reason', headerName: t('tireCardHistory.reason', 'Reason') }
    ],
    [t]
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(searchRaw);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchRaw]);

  const { data, totalRecord, isLoading } = useTireTransactionSearch(serialNumber, page, rowsPerPage, searchField, searchValue);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const getExportCellValue = (row, field) => {
    switch (field) {
      case 'transaction_date':
        return row.transaction_date ? dayjs(row.transaction_date).format('DD MMM YYYY') : '-';
      case 'serial_number':
        return row.serial_number || '-';
      case 'transaction_type':
        return row.transaction_type || '-';
      case 'tire_spec_name':
        return row.tire_spec_name || '-';
      case 'tire_brand_name':
        return row.tire_brand_name || '-';
      case 'tire_size_code':
        return row.tire_size_code_internal || row.tire_size_code || '-';
      case 'license_plate':
        return row.license_plate || '-';
      case 'km_unit':
        return row.km_unit ?? '-';
      case 'hm_unit':
        return row.hm_unit ?? '-';
      case 'current_position':
        return findPositionLabel(row.current_position, row.serial_number);
      case 'tire_damage_name':
        return row.tire_damage_name || '-';
      case 'air_pressure':
        return row.air_pressure ?? '-';
      case 'tread_depth':
        return row.tread_depth ?? '-';
      case 'mechanic_name':
        return row.mechanic_name || '-';
      case 'cost_repair':
        return row.cost_repair != null ? `Rp ${Number(row.cost_repair).toLocaleString('id-ID')}` : '-';
      case 'vendor_name':
        return row.vendor_name || '-';
      case 'work_order_number':
        return row.work_order_number || '-';
      case 'vendor_type':
        return row.vendor_type || '-';
      case 'repair_type':
        return row.repair_type || '-';
      case 'notes':
        return row.notes || '-';
      case 'action_status':
        return row.action_status || '-';
      case 'final_rtd':
        return row.final_rtd ?? '-';
      case 'reason':
        return row.reason || '-';
      default:
        return '-';
    }
  };

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await exportTireTransactionSearch(serialNumber, searchField, searchValue, totalRecord);
      const formattedData = dataToExport.map((row) => {
        const rowValues = {};
        EXPORT_COLUMNS.forEach((col) => {
          rowValues[col.headerName] = getExportCellValue(row, col.field);
        });
        return rowValues;
      });
      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `historical-tire-${getFormattedDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await exportTireTransactionSearch(serialNumber, searchField, searchValue, totalRecord);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('History');
      worksheet.columns = EXPORT_COLUMNS.map((col) => ({ header: col.headerName, key: col.field, width: 20 }));

      dataToExport.forEach((row) => {
        const rowValues = {};
        EXPORT_COLUMNS.forEach((col) => {
          rowValues[col.field] = getExportCellValue(row, col.field);
        });
        worksheet.addRow(rowValues);
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: 'center' };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `historical-tire-${getFormattedDate()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await exportTireTransactionSearch(serialNumber, searchField, searchValue, totalRecord);
      const doc = new jsPDF('landscape');
      doc.setFontSize(14);
      doc.text(`${t('tireCardHistory.title', 'Tire Card History')} - ${serialNumber}`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${dayjs().format('YYYY-MM-DD HH:mm')}`, 14, 22);

      const tableData = dataToExport.map((row) => EXPORT_COLUMNS.map((col) => getExportCellValue(row, col.field)));

      autoTable(doc, {
        head: [EXPORT_COLUMNS.map((col) => col.headerName)],
        body: tableData,
        startY: 30,
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [25, 118, 210] }
      });

      doc.save(`historical-tire-${getFormattedDate()}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#ffffff',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="800" color="text.primary">
            {t('tireCardHistory.title', 'Tire Card History')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('tireCardHistory.historicalSn', 'Historical transactions for SN:')}{' '}
            <Box component="span" fontWeight="700">
              {serialNumber}
            </Box>
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            bgcolor: '#f1f5f9',
            '&:hover': { bgcolor: '#e2e8f0', color: 'error.main' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#fbfcfd', display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar Section */}
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
          <TextField
            select
            size="small"
            value={searchField}
            onChange={(e) => {
              setSearchField(e.target.value);
              setSearchRaw('');
              setSearchValue('');
              setPage(0);
            }}
            sx={{ width: { xs: '100%', sm: 180 } }}
          >
            <MenuItem value="all">{t('general.all', 'All')}</MenuItem>
            <MenuItem value="serial_number">{t('tyreItem.serialNumber', 'Serial Number')}</MenuItem>
            <MenuItem value="license_plate">{t('appSetting.licensePlate', 'License Plate')}</MenuItem>
            <MenuItem value="transaction_type">{t('tireCardHistory.actionType', 'Transaction Type')}</MenuItem>
          </TextField>

          <TextField
            variant="outlined"
            placeholder={t('tireCardHistory.searchHistory', 'Search history...')}
            size="small"
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              )
            }}
          />
          <Box>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<DownloadIcon />}
              onClick={handleExportClick}
              disabled={isExporting}
              sx={{ bgcolor: '#ffffff', whiteSpace: 'nowrap' }}
            >
              {isExporting ? t('tireCardHistory.exporting', 'Exporting...') : t('tireCardHistory.export', 'Export')}
            </Button>
            <Menu anchorEl={exportAnchorEl} open={exportOpen} onClose={handleExportClose}>
              <MenuItem onClick={exportToCSV}>{t('tireCardHistory.exportCsv', 'Export to CSV')}</MenuItem>
              <MenuItem onClick={exportToExcel}>{t('tireCardHistory.exportExcel', 'Export to Excel')}</MenuItem>
              <MenuItem onClick={exportToPDF}>{t('tireCardHistory.exportPdf', 'Export to PDF')}</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Table Section */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <HtmlTableContainer>
            <HtmlTable style={{ minWidth: 1200 }}>
              <thead>
                <tr>
                  <StyledTableCell>{t('tyreItem.serialNumber', 'Serial Number')}</StyledTableCell>
                  <StyledTableCell>{t('general.date', 'Date')}</StyledTableCell>
                  <StyledTableCell>{t('tireCardHistory.actionType', 'Action Type')}</StyledTableCell>
                  <StyledTableCell>{t('specification.spec_name', 'Specification')}</StyledTableCell>
                  <StyledTableCell>{t('tireCardHistory.vehicle', 'Vehicle')}</StyledTableCell>
                  <StyledTableCell>{t('tireCardHistory.kmUnit', 'KM Unit')}</StyledTableCell>
                  <StyledTableCell>{t('tireCardHistory.hmUnit', 'HM Unit')}</StyledTableCell>
                  <StyledTableCell>{t('general.position', 'Position')}</StyledTableCell>
                  <StyledTableCell>{t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)')}</StyledTableCell>
                  <StyledTableCell>{t('tireCardHistory.treadDepthMm', 'Tread Depth (mm)')}</StyledTableCell>
                  <StyledTableCell>{t('tireCardHistory.mechanicName', 'Mechanic Name')}</StyledTableCell>
                  <StyledTableCell align="center">{t('details', 'Detail')}</StyledTableCell>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="12" align="center" style={{ padding: '64px 0' }}>
                      <CircularProgress size={32} thickness={5} />
                    </td>
                  </tr>
                ) : !data || data.length === 0 ? (
                  <tr>
                    <td colSpan="12" align="center" style={{ padding: '64px 0' }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('tireCardHistory.noDataFound', 'No transactions found.')}
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => {
                    const actionStyles = getActionStyles(row.transaction_type);
                    const hm = row.hm_unit != null ? row.hm_unit : 0;
                    const km = row.km_unit != null ? row.km_unit : 0;

                    return (
                      <StyledTableRow key={row.id || index}>
                        <td style={{ fontWeight: 600 }}>{row.serial_number || '-'}</td>
                        <td>{row.transaction_date ? dayjs(row.transaction_date).format('DD MMM YYYY') : '-'}</td>
                        <td>
                          <Chip
                            label={row.transaction_type || '-'}
                            size="small"
                            sx={{
                              bgcolor: actionStyles.bg,
                              color: actionStyles.color,
                              fontWeight: 700,
                              borderRadius: 1.5,
                              letterSpacing: 0.5
                            }}
                          />
                        </td>
                        <td>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body2">{row.tire_spec_name || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.tire_brand_name || '-'} - {row.tire_size_code_internal || row.tire_size_code || '-'}
                            </Typography>
                          </Box>
                        </td>
                        <td>{row.license_plate || '-'}</td>
                        <td>{Number(km).toLocaleString('id-ID')}</td>
                        <td>{Number(hm).toLocaleString('id-ID')}</td>
                        <td>{findPositionLabel(row.current_position, row.serial_number)}</td>
                        <td>{row.air_pressure != null ? row.air_pressure : '-'}</td>
                        <td>{row.tread_depth != null ? row.tread_depth : '-'}</td>
                        <td>{row.mechanic_name || '-'}</td>
                        <td align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSelectedRow(row);
                              setDetailModalOpen(true);
                            }}
                            sx={{ textTransform: 'none', minWidth: '80px' }}
                          >
                            {t('details', 'Detail')}
                          </Button>
                        </td>
                      </StyledTableRow>
                    );
                  })
                )}
              </tbody>
            </HtmlTable>
          </HtmlTableContainer>
        </Box>

        {/* Pagination Section */}
        <Box sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
          <TablePagination
            component="div"
            count={totalRecord || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                mb: 0
              }
            }}
          />
        </Box>
      </DialogContent>

      <TransactionDetailModal open={detailModalOpen} onClose={() => setDetailModalOpen(false)} data={selectedRow} />
    </Dialog>
  );
}
